import React, { useState, useContext } from 'react';
import TrashIcon from '@/assets/remove.svg';
import DownloadIcon from '@/assets/downloadSimpleLight.svg';
import ScissorsIcon from '@/assets/scissors-light.svg';
import { Color, Palette } from '../../types/ColorLibraryModel';
import { isColorDark, hexToRgb, colorFromBgColor } from '../../utils/color-library-utils';
import { UserContext } from '../ContextProviders';
import { saveToScrapbook } from '../../api/scrapbook-data';
import FolderSelectionDialog from '../scrapbook/dialogs/FolderSelectionDialog';
import Tooltip from '../general/Tooltip';
import { useNotification } from '../general/NotificationProvider';
import Dropdown from '../formControl/Dropdown';
import classNames from 'classnames';

interface ColorDetailsProps {
  color: Color;
  colors?: Color[];
  palettes?: Palette[];
  showRemoveButton?: boolean;
  showCreatePaletteText?: boolean;
  showDownloadButton?: boolean;
  showSaveToScrapbookButton?: boolean;
  showAddToPaletteButton?: boolean;
  clickedPalette?: Palette | null;
  onAddColorToPalette?: (palette: Palette) => void;
  onRemoveColorFromPalette?: (colorId: string) => void;
  onDownloadColor?: (colors: Color[]) => void;
}

const ColorDetails: React.FC<ColorDetailsProps> = ({
  color,
  colors = [],
  palettes = [],
  showRemoveButton = false,
  showCreatePaletteText = false,
  showDownloadButton = true,
  showSaveToScrapbookButton = true,
  showAddToPaletteButton = true,
  onAddColorToPalette,
  onRemoveColorFromPalette,
  onDownloadColor,
}) => {
  const [chosenPaletteId, setChosenPaletteId] = useState<string>('');
  const [showFolderDialog, setShowFolderDialog] = useState(false);
  const user = useContext(UserContext);
  const { showNotification } = useNotification();

  const isDark = isColorDark(color.hexCode);
  const rgbValues = hexToRgb(color.hexCode);

  const handleAddColorToSelectedPalette = () => {
    if (!chosenPaletteId) return;

    const currentPalette = palettes.find((p) => String(p.id) === chosenPaletteId);
    if (!currentPalette) return;

    let hasNewColors = false;
    const colorsToAdd = colors.length > 0 ? colors : [color];

    colorsToAdd.forEach((colorItem) => {
      const exists = currentPalette.colours.find((c) => c.id === colorItem.id);
      if (!exists) {
        hasNewColors = true;
        currentPalette.colours.unshift({ ...colorItem });
      }
    });

    if (hasNewColors) {
      onAddColorToPalette?.({ ...currentPalette });
    }
  };

  const handleDownload = () => {
    const colorsToDownload = colors.length > 0 ? colors : [color];
    onDownloadColor?.(colorsToDownload);
  };

  const handleRemove = () => {
    onRemoveColorFromPalette?.(color.id);
  };

  const handleSaveToScrapbook = () => {
    if (!user.userId) return;
    setShowFolderDialog(true);
  };

  const handleSaveToFolder = async (selectedFolderId: number) => {
    try {
      const colorData = {
        path: `/colour.${color.id}.json`,
        contentType: 'application/x.stylus.colour.chip+json',
        content: JSON.stringify(color),
      };

      const result = await saveToScrapbook(selectedFolderId, colorData, user.userId);

      if (result?.succeeded) {
        showNotification(result.message || 'Color saved to scrapbook successfully', 'savesuccess');
      } else {
        showNotification('Call to scrapbook server failed.', 'error');
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      showNotification('Call to scrapbook server failed.', 'error');
    }
  };

  const getEffectClass = () => {
    if (color.effect === 'Metallic') return 'bg-metallic';
    if (color.effect === 'Lustre') return 'bg-lustre';
    if (color.effect === 'Chrome') return 'bg-chrome';
    return '';
  };

  return (
    <div
      className={`min-h-auto w-full rounded-sm bg-cover bg-center bg-no-repeat transition-all duration-200 md:min-h-[330px] ${isDark ? 'text-white' : 'text-black'} ${getEffectClass()}`}
      style={{
        backgroundColor: `#${color.hexCode}`,
      }}
    >
      <div className="p-4 pt-17 sm:p-6 sm:pt-17 md:p-4 md:pt-8 lg:p-6 lg:pt-12">
        <div className="mb-3 flex items-center justify-between sm:mb-4 md:mb-4 lg:mb-6">
          <h3 className="text-base font-medium sm:text-lg md:text-lg lg:text-3xl">{color.displayName}</h3>
          {showRemoveButton && (
            <button onClick={handleRemove} className="transition-all duration-200 hover:scale-110">
              <TrashIcon className="h-5 w-5 cursor-pointer sm:h-6 sm:w-6 lg:h-7 lg:w-7" />
            </button>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3 pt-4 sm:pt-6 md:pt-6 lg:gap-4 lg:pt-7">
          <div>
            <div className="mb-1 text-xs font-semibold lg:text-sm">Pantone</div>
            <div className="mb-3 text-sm sm:text-base lg:mb-4 lg:text-base">{color.tpxCode}</div>
            <div className="mb-1 text-xs font-semibold lg:text-sm">Hex</div>
            <div className="mb-3 text-sm sm:text-base lg:mb-4 lg:text-base">#{color.hexCode}</div>
            <div className="mb-1 text-xs font-semibold lg:text-sm">RAL</div>
            <div className="mb-3 text-sm sm:text-base lg:mb-4 lg:text-base">{color.ralCode}</div>
          </div>
          <div>
            <div className="mb-1 text-xs font-semibold lg:text-sm">Coated</div>
            <div className="mb-3 text-sm sm:text-base lg:mb-4 lg:text-base">{color.coatedCode}</div>
            <div className="mb-1 text-xs font-semibold lg:text-sm">RGB</div>
            <div className="mb-3 text-sm sm:text-base lg:mb-4 lg:text-base">{rgbValues.join('/')}</div>
            <div className="mb-1 text-xs font-semibold lg:text-sm">NCS</div>
            <div className="mb-3 text-sm sm:text-base lg:mb-4 lg:text-base">{color.ncsCode}</div>
          </div>
        </div>

        {showAddToPaletteButton && user.userId && palettes.length > 0 && (
          <div className="mt-4 sm:mt-5 md:mt-5 lg:mt-6">
            <div
              className="flex h-10 items-center rounded-full border px-3 py-2 transition-all duration-200 hover:shadow-md sm:h-11 sm:px-4 md:h-11 md:px-3"
              style={{ borderColor: colorFromBgColor(color.hexCode, 0.2) }}
            >
              <div className="w-full">
                <Dropdown
                  placeholder="Palettes"
                  name="palette"
                  options={[...palettes.map((p) => ({ label: p.name, value: String(p.id) }))]}
                  value={chosenPaletteId || ''}
                  onChange={(e) => setChosenPaletteId(e.target.value || '')}
                  className={classNames(
                    '[&_.main-button]:h-auto [&_.main-button]:w-full [&_.main-button]:cursor-pointer [&_.main-button]:border-none [&_.main-button]:bg-transparent [&_.main-button]:p-0 [&_.main-button]:text-center [&_.main-button]:text-sm [&_.main-button]:outline-none lg:[&_.main-button]:text-base [&_.popover-title]:line-clamp-1',
                    isDark && '[&_.arrow_svg]:fill-white',
                  )}
                  contentWrapperClassName="[&_.content]:bg-white [&_.content]:text-black"
                />
              </div>
              <div className="mx-2 h-4 w-px" style={{ backgroundColor: colorFromBgColor(color.hexCode, 0.2) }} />
              <button
                onClick={handleAddColorToSelectedPalette}
                disabled={!chosenPaletteId}
                className="w-full cursor-pointer text-sm font-semibold transition-all duration-200 hover:opacity-80 disabled:opacity-50 lg:text-[15px]"
                style={{ color: isDark ? 'white' : 'black' }}
              >
                Add to Palette
              </button>
            </div>

            {!showCreatePaletteText && (
              <p className="mt-3 hidden text-center text-sm font-semibold md:block lg:mt-4 lg:text-base">
                Create a palette in My Palettes
              </p>
            )}
          </div>
        )}

        {(showDownloadButton || showSaveToScrapbookButton) && (
          <div className="mt-4 flex gap-3 sm:mt-5 md:mt-5 lg:mt-6 lg:gap-4">
            {showDownloadButton && (
              <Tooltip placement="bottom" content="Download">
                <button
                  onClick={handleDownload}
                  className="bg-transparent p-2 transition-all duration-200 hover:scale-110"
                  title="Download"
                >
                  <DownloadIcon
                    className={`h-5 w-5 cursor-pointer sm:h-6 sm:w-6 md:h-6 md:w-6 lg:h-7 lg:w-7 ${isDark ? 'brightness-0 contrast-100 invert' : ''}`}
                  />
                </button>
              </Tooltip>
            )}
            {showSaveToScrapbookButton && (
              <Tooltip placement="bottom" content="Save to Scrapbook">
                <button
                  onClick={handleSaveToScrapbook}
                  className="cursor-pointer bg-transparent p-2 transition-all duration-200 hover:scale-110"
                  title="Save to Scrapbook"
                >
                  <ScissorsIcon
                    className={`h-5 w-5 sm:h-6 sm:w-6 md:h-6 md:w-6 lg:h-7 lg:w-7 ${isDark ? 'brightness-0 contrast-100 invert' : ''}`}
                  />
                </button>
              </Tooltip>
            )}
          </div>
        )}
      </div>

      <FolderSelectionDialog
        isOpen={showFolderDialog}
        onClose={() => setShowFolderDialog(false)}
        onSave={handleSaveToFolder}
        reportTitle={color.displayName}
      />
    </div>
  );
};

export default ColorDetails;
